import React from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle, FaHome } from "react-icons/fa";

const planLabels: Record<string, string> = {
  small: "Small Plan (4–50 beds)",
  medium: "Medium Plan (51–80 beds)",
  "large-medium": "Large-Medium Plan (81–125 beds)",
  large: "Large Plan (126+ beds)",
};

const formatUsdWhole = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const PaymentSuccess: React.FC = () => {
  const location = useLocation();
  const state = (location.state as any) || {};
  const [searchParams] = useSearchParams();

  const planKey = searchParams.get("plan") || state.plan || "";
  const planLabel =
    planLabels[planKey] || state.plan || planKey || "Your plan";
  const billingPrepay = searchParams.get("billing") === "prepay";
  const prepayTotalRaw = searchParams.get("prepayTotal");
  const prepayTotal = prepayTotalRaw ? parseInt(prepayTotalRaw, 10) : NaN;
  const monthlyTotalRaw = searchParams.get("monthlyTotal");
  const monthlyTotal = monthlyTotalRaw ? parseInt(monthlyTotalRaw, 10) : NaN;

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, type: "spring" }}
      className="max-w-2xl mx-auto py-12 px-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: "spring" }}
        className="bg-white rounded-3xl shadow-lg p-12 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <FaCheckCircle className="text-6xl text-green-500 mx-auto" />
        </motion.div>
        <h1 className="text-3xl font-extrabold text-blue-800 mb-4">
          Payment Successful!
        </h1>
        <p className="text-lg text-gray-700 mb-6">
          {billingPrepay
            ? "Thank you for your purchase. Your 3-year prepaid term is being finalized."
            : "Thank you for your purchase. Your subscription has been activated."}
        </p>
        {planKey && (
          <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left">
            <h2 className="text-xl font-bold text-blue-800 mb-4">
              Order Details
            </h2>
            <div className="space-y-2 text-gray-700">
              <div className="flex justify-between gap-4">
                <span>Plan:</span>
                <span className="font-semibold text-right">{planLabel}</span>
              </div>
              {billingPrepay && !Number.isNaN(prepayTotal) ? (
                <>
                  <div className="flex justify-between gap-4">
                    <span>Billing:</span>
                    <span className="font-semibold text-right">
                      3-year prepay (10% savings)
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>Prepay total:</span>
                    <span className="font-semibold text-right">
                      {formatUsdWhole(prepayTotal)}
                    </span>
                  </div>
                  {!Number.isNaN(monthlyTotal) && (
                    <div className="flex justify-between gap-4 text-sm text-gray-600">
                      <span>Monthly equivalent (before discount):</span>
                      <span className="text-right">
                        {formatUsdWhole(monthlyTotal)}/mo
                      </span>
                    </div>
                  )}
                </>
              ) : !Number.isNaN(monthlyTotal) ? (
                <div className="flex justify-between gap-4">
                  <span>Monthly total:</span>
                  <span className="font-semibold text-right">
                    {formatUsdWhole(monthlyTotal)}/mo
                  </span>
                </div>
              ) : (
                state.total && (
                  <div className="flex justify-between gap-4">
                    <span>Monthly Total:</span>
                    <span className="font-semibold text-right">
                      ${state.total}/mo
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        <p className="text-gray-600 mb-8">
          A confirmation email has been sent to your email address.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition font-semibold"
        >
          <FaHome /> Return to Home
        </Link>
      </motion.div>
    </motion.section>
  );
};

export default PaymentSuccess;

